import { ChangeDetectionStrategy, ChangeDetectorRef, Component, input, output, signal, effect, DestroyRef } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { JsonDeliveryHole } from 'src/app/bo/delivery';
import { DeliveryParams, HoleCondition, Detonator } from 'src/app/services/hole.service';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { TranslateService } from '@ngx-translate/core';
import { HoleQuantityValidationService } from '../hole-quantity-validation.service';

/** What an update request saved for one hole: its form index and the values sent. */
export interface SavedHole {
  index: number;
  values: unknown;
}

/** One Confirm of the hole editor: the delivery modal calls `onSaved` once this update request succeeded. */
export interface HoleSaveRequest {
  onSaved: (savedHoles: SavedHole[]) => void;
}

@Component({
  selector: 'app-modify-hole',
  templateUrl: './modify-hole.component.html',
  styleUrls: ['./modify-hole.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ModifyHoleComponent {

  selectedHoleIndex = signal<number>(-1);
  isDetailsVisible = signal<boolean[]>([]);
  form = signal<FormGroup | undefined>(undefined);
  detosLibelle = signal<string>('');
  selectedConditions = signal<HoleCondition[]>([]);
  detonators = signal<Detonator[]>([]);
  destDeliveryName = signal<string>('');
  initialValues = signal<any[]>([]);

  totalHoles = signal<number>(0);
  isLoading = signal<boolean>(false);
  isInitializing = signal<boolean>(true);

  holesDeliveryControl = input.required<FormArray>();
  deliveryParams = input.required<DeliveryParams>();
  totalHolesCount = input<number>(0);
  currentLoadedCount = input<number>(0);
  isScrollLoading = input<boolean>(false);
  /** True while the delivery is being saved: Confirm and Cancel wait for the response. */
  isSaving = input<boolean>(false);

  updateDelivery = output<HoleSaveRequest>();
  splitDelivery = output<string>();
  loadMoreHoles = output<void>();

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef,
    private readonly fonctionsService: FonctionsService,
    private readonly translate: TranslateService,
    private readonly quantityValidation: HoleQuantityValidationService
  ) {
    effect(() => {
      const params = this.deliveryParams();
      if (params) {
        this.detonators.set(params.detonators.map((deto) => ({
          commercialName: deto.commercialName,
          productId: deto.product.id,
          product: deto.product,
          id: deto.id,
        })));
      }
    });

    effect(() => {
      const total = this.totalHolesCount();
      this.totalHoles.set(total);
    });
  }

  ngOnInit(): void {
    const tempVisible: boolean[] = [];
    const tempInitial: any[] = [];

    this.holesDeliveryControl().controls.forEach((holeControl, index) => {
      tempVisible.push(false);
      tempInitial.push(JSON.parse(JSON.stringify(holeControl.value)));

      this.ensureMinimumDetonators(holeControl as FormGroup);
    });

    this.isDetailsVisible.set(tempVisible);
    this.initialValues.set(tempInitial);

    this.isInitializing.set(false);
    this.changeDetectorRef.detectChanges();
  }

  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const threshold = 200;

    if (element.scrollTop + element.clientHeight >= element.scrollHeight - threshold) {
      if (!this.isScrollLoading() && this.currentLoadedCount() < this.totalHolesCount()) {
        this.loadMoreHoles.emit();
      }
    }
  }

  private ensureMinimumDetonators(holeFormGroup: FormGroup): void {
    const detosControl = holeFormGroup.get('detos') as FormArray;
    if (detosControl && detosControl.length === 0) {
      while (detosControl.length < 2) {
        detosControl.push(new FormGroup({
          productId: new FormControl(null),
          count: new FormControl(null),
        }));
      }
    }
  }

  getDetosCount(form: FormGroup): number {
    let detos = form.get('detos')?.value;
    let detosCount = 0;
    if (detos) {
      for (let i = 0; i < detos.length; i++) {
        const deto = detos[i];
        // An empty detonator row counts for nothing (null would turn the total into NaN).
        detosCount += Number(deto.count) || 0;
      }
    }
    return detosCount;
  }

  toggleDetails(index: number): void {
    this.captureInitialValue(index);
    this.isDetailsVisible.update(arr => {
      const newArr = [...arr];
      newArr[index] = !newArr[index];
      return newArr;
    });
    this.selectedHoleIndex.set(index);
  }

  truncateText(text: string): string {
    if (text.length > 20) {
      return text.substring(0, 20) + '...';
    }
    return text;
  }

  getHoleConditionLabel(condition: HoleCondition[]): string {
    return condition.map(c => c.shortName).join('- ');
  }

  clearConditions() {
    this.selectedConditions.set([]);
  }

  onSubmit(index: number) {
    if (this.isSaving()) return;

    const hole = this.holesDeliveryControl().at(index);
    // Checked here to keep the hole open; the delivery modal checks every submitted hole again before saving.
    if (!this.quantityValidation.isHoleValid(hole, this.initialValues()[index])) {
      this.fonctionsService.launch_toast(false, false,
        this.translate.instant('DELIVERY.INVALID_QUANTITIES', { hole: hole.get('holeId')?.value ?? '' }));
      return;
    }
    this.updateDelivery.emit({ onSaved: savedHoles => this.onHolesSaved(index, savedHoles) });
  }

  /**
   * Every hole saved by the request (the confirmed one and any other edited hole sent with it) gets
   * the saved values as its reference for Cancel and the next checks. The confirmed hole closes unless
   * it was edited while the request was running: the delivery modal then left it dirty, as unsaved.
   * A refused or failed save never calls this, so the hole stays open.
   */
  private onHolesSaved(confirmedIndex: number, savedHoles: SavedHole[]): void {
    this.initialValues.update(values => {
      const next = [...values];
      for (const saved of savedHoles) next[saved.index] = saved.values;
      return next;
    });

    if (this.holesDeliveryControl().at(confirmedIndex)?.dirty) return;

    this.isDetailsVisible.update(visible => {
      const next = [...visible];
      next[confirmedIndex] = false;
      return next;
    });
  }

  onSplitDelivery(destDeliveryName: string) {
    this.splitDelivery.emit(destDeliveryName);
  }

  protected resetForGroup(formGroup: FormGroup): void {
    formGroup.reset();
  }

  /**
   * Holes appended by the infinite scroll are not in the snapshot taken in ngOnInit: their loaded
   * value is captured when their details are first opened, i.e. before any edit.
   */
  private captureInitialValue(index: number): void {
    if (this.initialValues()[index]) return;
    const hole = this.holesDeliveryControl().at(index);
    if (!hole) return;

    this.initialValues.update(values => {
      const next = [...values];
      next[index] = JSON.parse(JSON.stringify(hole.value));
      return next;
    });
  }

  onCancel(formGroup: FormGroup, index: number) {
    // A pending save will update the reference: cancelling now would restore outdated values.
    if (this.isSaving()) return;
    const original = this.initialValues()[index];
    if (!original) return;
    formGroup.patchValue(original);
    // The restored values are complete numbers again (detonator controls are recreated below).
    ['cartdridge', 'booster'].forEach(name => {
      const control = formGroup.get(name);
      if (control) this.quantityValidation.markComplete(control);
    });
    const detosControl = formGroup.get('detos') as FormArray;
    if (detosControl) {
      detosControl.clear();

      for (const deto of original.detos) {
        detosControl.push(new FormGroup({
          productId: new FormControl(deto.productId),
          count: new FormControl(deto.count),
        }));
      }
      // Same empty rows as when the hole was first displayed.
      this.ensureMinimumDetonators(formGroup);
    }
    // Back to the saved values: nothing left to send for this hole.
    formGroup.markAsPristine();
    this.toggleDetails(index);
  }
  // Quantities are summed from floats (three 1.55 loads give 4.6499996): drop that noise before display
  // rounding so the UI shows the same one-decimal value as the Excel reports.
  snapQuantity(value: number | null | undefined): number | null {
    if (value === null || value === undefined) return null;
    return Math.round(Number(value) * 10000) / 10000;
  }

  /** Last accepted text of each quantity input, restored when an input is refused. */
  private readonly lastValidQuantity = new WeakMap<AbstractControl, string>();

  /** Starting point of the edit: what the input shows when it gets the focus. */
  rememberQuantity(event: Event, control: AbstractControl | null): void {
    if (control) this.lastValidQuantity.set(control, (event.target as HTMLInputElement).value);
  }

  /**
   * Cartridge quantities take at most one decimal. A second decimal is refused as it is typed
   * (the input keeps its previous value): the value is never rounded.
   */
  limitToOneDecimal(event: Event, control: AbstractControl | null): void {
    this.acceptQuantity(event, control, /^\d*(\.\d?)?$/);
  }

  /** Booster and detonator quantities are whole numbers: a decimal (typed or pasted) is refused, never rounded. */
  limitToInteger(event: Event, control: AbstractControl | null): void {
    this.acceptQuantity(event, control, /^\d*$/);
  }

  /**
   * Clipboard and drag-and-drop: a quantity that follows the field rule goes in, any other text is refused
   * (e.g. 40.8 in a booster, 1.25 in a cartridge). The input handler still checks the resulting value.
   */
  allowQuantityText(event: ClipboardEvent | DragEvent, decimals: 0 | 1): void {
    const transfer = 'clipboardData' in event ? event.clipboardData : event.dataTransfer;
    const text = (transfer?.getData('text') ?? '').trim();
    const allowed = decimals === 0 ? /^\d+$/ : /^\d+(\.\d)?$/;
    if (!allowed.test(text)) event.preventDefault();
  }

  /** Blocks the decimal separators on whole-number quantities, on top of the scientific notation. */
  preventDecimalInput(event: KeyboardEvent): void {
    if (event.key === '.' || event.key === ',') {
      event.preventDefault();
      return;
    }
    this.preventScientificInput(event);
  }

  /** Keeps the input when its text matches `allowed`, otherwise restores the last accepted text. */
  private acceptQuantity(event: Event, control: AbstractControl | null, allowed: RegExp): void {
    if (!control) return;
    const input = event.target as HTMLInputElement;
    // "1." while typing: the browser reports an empty value, keep the last accepted text
    // and remember the entry is unfinished until it becomes a number or is cleared.
    if (input.validity.badInput) {
      this.quantityValidation.markIncomplete(control);
      return;
    }
    this.quantityValidation.markComplete(control);

    if (allowed.test(input.value)) {
      this.lastValidQuantity.set(control, input.value);
      return;
    }

    const previous = this.lastValidQuantity.get(control) ?? '';
    input.value = previous;
    control.setValue(previous === '' ? null : Number(previous));
  }

  preventScientificInput(event: KeyboardEvent): void {
    if (event.key === 'e' || event.key === 'E' || event.key === '+' || event.key === '-') {
      event.preventDefault();
    }
  }
}
